import { twMerge } from 'tailwind-merge'
import clsx from 'clsx'

export function cn (...inputs) {
  return twMerge(clsx(inputs))
}

export const _prepareMetaDataResponse = (data) => {
  const metadata = {
    applicationName: 'Gumlet',
    charset: 'utf-8',
    icons: {
      icon: 'https://assets.gumlet.io/assets/round-logo.png?w=196',
      apple: 'https://assets.gumlet.io/assets/round-logo.png?w=180'
    },
    robots: {
      googleBot: 'noindex,indexifembedded'
    },
    openGraph: {},
    twitter: {},
    alternates: {
      canonical: `https://play.gumlet.io/embed/${data?.videoId}`,
      types: {
        'application/json+oembed': `https://api.gumlet.com/v1/oembed?url=https://play.gumlet.io/embed/${data?.videoId}&format=json`,
        'text/xml+oembed': `https://api.gumlet.com/v1/oembed?url=https://play.gumlet.io/embed/${data?.videoId}&format=xml`
      }
    }
  }

  const metaTitle = data?.videoTitle
  const metaDescription = data?.videoDescription
  const metaUrl = 'https://play.gumlet.io/embed/' + data?.videoId
  const metaImage = data?.poster
  const playBackUrl = data?.playbackURL
  const metaWidth = data?.dimensions?.videoWidth
  const metaHeight = data?.dimensions?.videoHeight
  const metaType = 'video'
  const metaLocale = 'en_US'
  const metaDuration = data?.durationISO
  // const metaAspectRatio = data?.aspectRatio
  const metaSiteName = 'Gumlet'
  const metaVideoUrl = data?.playbackURL

  metadata.title = metaTitle
  metadata.description = metaDescription
  metadata.openGraph = {
    // "type": meta_type,
    locale: metaLocale,
    url: metaUrl,
    site_name: metaSiteName,
    title: metaTitle,
    description: metaDescription,
    video: {
      url: metaVideoUrl,
      secure_url: metaVideoUrl,
      type: metaType,
      width: metaWidth,
      height: metaHeight,
      duration: metaDuration
    }
  }

  metadata.twitter = {
    url: metaUrl,
    card: 'summary_large_image',
    site: '@gumlethq',
    title: metaTitle,
    description: metaDescription,
    player: {
      height: metaHeight,
      width: metaWidth,
      stream: playBackUrl
    },
    image: {
      src: metaImage,
      altText: metaTitle
    }
  }

  return metadata
}

export const _prepareJsonSchema = (data, videoId) => {
  const settings = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: data?.videoTitle,
    description: data?.videoDescription ? convertHtmlToPlainText(data?.videoDescription) : '',
    thumbnailUrl: data?.thumbnail || data?.poster,
    datePublished: new Date(data?.createdAt).toString(),
    uploadDate: new Date(data?.uploadedAt).toString(),
    duration: data?.durationISO,
    contentUrl: data?.playbackURL,
    embedUrl: 'https://play.gumlet.io/embed/' + data?.videoId,
    width: data?.dimensions?.videoWidth,
    height: data?.dimensions?.videoHeight,
    thumbnail: {
      '@type': 'ImageObject',
      contentUrl: data?.thumbnail || data?.poster,
      width: data?.dimensions?.videoWidth,
      height: data?.dimensions?.videoHeight
    }
  }

  if (data?.chapters?.length) {
    settings.hasPart = data?.chapters.map((chapter, index) => {
      return {
        '@type': 'Clip',
        name: chapter?.label ? chapter.label : `Chapter ${index + 1}`,
        startOffset: index === 0 ? 0 : data.chapters[index - 1].endTime,
        endOffset: chapter.endTime,
        url: `https://play.gumlet.io/embed/${videoId}?t=${index === 0 ? 0 : data.chapters[index - 1].endTime}`
      }
    })
  }
  if (data?.captions?.length) {
    let obj = data.captions[0]
    data.captions.forEach(element => {
      if (element.default) obj = element
    })
    settings.caption = {
      '@type': 'MediaObject',
      encodingFormat: 'text/vtt',
      contentUrl: obj.src
    }
    settings.transcript = obj.src
  }
  return settings
}

export const convertHtmlToPlainText = (string) => {
  return string.replace(/<[^>]+>/g, '')
    .replace(/\n/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, '')
}

export const structureLiveStreamDate = (dateString) => {
  const date = new Date(dateString)

  // Extract hours, minutes, and seconds
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')

  // Format the day with ordinal suffix
  const day = date.getDate()
  let suffix
  if (day === 1 || day === 21 || day === 31) {
    suffix = 'st'
  } else if (day === 2 || day === 22) {
    suffix = 'nd'
  } else if (day === 3 || day === 23) {
    suffix = 'rd'
  } else {
    suffix = 'th'
  }
  // Get the month name
  const month = date.toLocaleString('en-US', { month: 'long' })

  return `${hours}:${minutes}:${seconds} on ${day}${suffix} ${month}`
}

// get chapter start time and thumbnails in response

export const prepareVideoChapters = (chapters, poster) => {
  if (!chapters || !Array.isArray(chapters) || chapters.length === 0) {
    return []
  }
  const allChapters = []
  // itrate the chapter and prepare the start time.
  // start time condition.
  // first chapter start time is 0 always
  // next chapter start time is previous chapter endTime
  // also return start time, label
  // also total duration of the chapter

  chapters.forEach((chapter, index) => {
    const start = index === 0 ? 0 : chapters[index - 1].endTime
    allChapters.push({
      startTime: start,
      endTime: chapter.endTime,
      label: chapter.label || `Chapter ${index + 1}`,
      thumbnail: `${poster}${poster.includes('?') ? '&time=' : '?time='}${start}`,
      totalDuration: chapter.endTime - start
    })
  })
  return allChapters
}

export const formatVideoSeconds = (seconds) => {
  const sec = Math.floor(seconds % 60)
  const min = Math.floor((seconds / 60) % 60)
  const hrs = Math.floor(seconds / 3600)

  const pad = (val) => String(val).padStart(2, '0')

  if (hrs > 0) {
    return `${pad(hrs)}:${pad(min)}:${pad(sec)}`
  } else {
    return `${pad(min)}:${pad(sec)}`
  }
}
